import { fr } from "date-fns/locale";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useProcedureHearingsByJuridiction, useUpsertNextHearing } from "../../api/use-claim-actions";
import type { CompetentJuridiction } from "./court-overview";
import {
  extractDatesFromJuridiction,
  formatHearingDay,
  getHearingsForDate,
  isDateInClaimPayload,
} from "./hearing-helpers";

/**
 * Port of overview/hearings/HearingSelection.vue — pick/add the next hearing of
 * the claim's procedure among the ongoing hearings of the competent courts.
 */
export function HearingSelection({
  claim,
  jurisdictions,
}: {
  claim: ClaimWithRelations;
  jurisdictions: CompetentJuridiction[];
}) {
  const { data: hearingsByJuridictions, isPending } = useProcedureHearingsByJuridiction(claim.id);
  const upsertNextHearing = useUpsertNextHearing(claim.id);

  const [selectedDateHearing, setSelectedDateHearing] = useState<string>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerHearingDate, setDatePickerHearingDate] = useState<Date | undefined>(new Date());

  const minHearingDate = new Date(new Date().toISOString().split("T")[0]);

  function selectHearingDate(date: string) {
    setSelectedDateHearing(date);
    setDatePickerHearingDate(undefined);
  }

  function setHearing(juridictionId: number) {
    upsertNextHearing.mutate({
      juridictionId,
      courtHearingId: 0, // TODO: pass the courtHearingId /!\ (kept from HearingSelection.vue)
      // TODO: Dynamic values (kept from HearingSelection.vue)
      hearingTypeId: 1,
      lawyerId: 1,
      procedureType: "PROCEDURE_CLASSIQUE",
    });
  }

  if (isPending) {
    return (
      <div className="px-6 pb-6">
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <Card className="mx-6 mb-6 p-4">
      {jurisdictions.map((juridiction) => {
        const dates = extractDatesFromJuridiction(hearingsByJuridictions, juridiction.name);
        const showPicker = dates.length === 0 || datePickerOpen;
        const canSaveFromPicker =
          datePickerOpen || (dates.length === 0 && datePickerHearingDate !== undefined);

        return (
          <Collapsible key={juridiction.id}>
            <CollapsibleTrigger className="group my-3 flex w-full items-center justify-between rounded-lg bg-muted px-4 py-2 text-left text-sm font-medium text-foreground hover:bg-accent">
              <span>
                {juridiction.name} : {dates.length} dates d'audiences possibles
              </span>
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-data-panel-open:rotate-180" />
            </CollapsibleTrigger>
            <CollapsiblePanel className="px-4 pt-4 pb-2 text-sm text-muted-foreground">
              {juridiction.code ? (
                <Button
                  variant="outline"
                  render={
                    // biome-ignore lint/a11y/useAnchorContent: content injected by Button children
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.infogreffe.fr/fiche-greffe/${juridiction.code}`}
                    />
                  }
                >
                  <CalendarDays />
                  Calendrier des audiences
                </Button>
              ) : null}

              {dates.length ? (
                <div className="my-4 grid grid-cols-3 gap-3">
                  {dates.map((date) => {
                    const alreadyBooked = isDateInClaimPayload(
                      hearingsByJuridictions,
                      date,
                      juridiction.name,
                      claim.id,
                    );
                    const bookedCount = getHearingsForDate(
                      hearingsByJuridictions,
                      juridiction.name,
                      date,
                    ).length;

                    return (
                      <button
                        key={date}
                        type="button"
                        disabled={alreadyBooked}
                        onClick={() => selectHearingDate(date)}
                        className={cn(
                          "flex flex-col items-center justify-center rounded-md border px-3 py-3 text-sm font-semibold capitalize sm:flex-1",
                          alreadyBooked
                            ? "cursor-auto border-transparent bg-primary/8"
                            : "cursor-pointer hover:bg-accent/50",
                          selectedDateHearing === date && "bg-primary/8 ring-2 ring-primary",
                        )}
                      >
                        <span className="text-foreground">{formatHearingDay(date)}</span>
                        <Badge variant="secondary" className="mt-1 capitalize">
                          {bookedCount}
                          {bookedCount > 1 ? " dossiers" : " dossier"}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="flex flex-col items-center justify-center">
                {dates.length === 0 ? <div className="my-2">Aucune audience prévue à ce tribunal</div> : null}

                {dates.length > 0 && !datePickerOpen && !selectedDateHearing ? (
                  <Button variant="outline" className="my-2" onClick={() => setDatePickerOpen(true)}>
                    Choisir une autre date
                  </Button>
                ) : null}

                {showPicker ? (
                  <Calendar
                    mode="single"
                    locale={fr}
                    selected={datePickerHearingDate}
                    onSelect={setDatePickerHearingDate}
                    disabled={{ before: minHearingDate }}
                  />
                ) : null}

                {canSaveFromPicker ? (
                  <Button
                    variant="outline"
                    className="my-2"
                    loading={upsertNextHearing.isPending}
                    onClick={() => {
                      if (datePickerHearingDate) {
                        setHearing(juridiction.id);
                      }
                    }}
                  >
                    Sauvegarder l'audience
                  </Button>
                ) : selectedDateHearing ? (
                  <Button
                    variant="outline"
                    className="my-2"
                    loading={upsertNextHearing.isPending}
                    onClick={() => setHearing(juridiction.id)}
                  >
                    Sauvegarder l'audience
                  </Button>
                ) : null}
              </div>
            </CollapsiblePanel>
          </Collapsible>
        );
      })}
    </Card>
  );
}
